import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Sparkles, Award, Users } from 'lucide-react';
import Reveal from '../components/Reveal';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-black pt-24 md:pt-40 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <Reveal className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            About <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Visual Creationz</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Photos. Videos. Music. Effects. Woven into cinematic stories built to last.
          </p>
        </Reveal>

        {/* Main Story */}
        <Reveal className="max-w-4xl mx-auto mb-20">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 lg:p-12 border border-gray-700">
            <div className="prose prose-lg prose-invert max-w-none">
              <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Visual Creationz was born from a simple belief: that every moment that matters deserves to be told beautifully. Whether it's honouring a life well-lived, celebrating a milestone, or capturing the magic of a wedding day, we craft stories that bring your photographs back to life.
              </p>
              <p className="text-gray-300 leading-relaxed mb-4">
                We weave <strong>photos, videos, music and effects</strong> into one cinematic story - every project built with care, emotion, and a real eye for detail. This isn't a photo compilation. It's a film made just for you.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Our commitment is simple: exceptional quality, transparent pricing, and the personal touch that makes every client feel like our only client.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Mission Statement */}
        <Reveal className="max-w-5xl mx-auto mb-20">
          <div className="bg-gradient-to-r from-cyan-900/20 to-blue-900/20 rounded-3xl p-8 lg:p-12 border border-cyan-500/30 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Our Mission
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
              To turn your photographs, footage and stories into cinematic films families will treasure for generations - blending professional craft with heartfelt care, one moment at a time.
            </p>
          </div>
        </Reveal>

        {/* Values */}
        <div className="max-w-6xl mx-auto mb-20">
          <Reveal className="text-3xl md:text-4xl font-bold text-white text-center mb-12" as="h2">
            What Drives <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Us</span>
          </Reveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Reveal delay={1} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 text-center hover:border-cyan-500 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full mx-auto mb-6">
                <Heart size={32} className="text-black" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Emotional Connection</h3>
              <p className="text-gray-400">
                We understand the deep emotional value of memories and treat every project with sensitivity and care.
              </p>
            </Reveal>

            <Reveal delay={2} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 text-center hover:border-cyan-500 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full mx-auto mb-6">
                <Sparkles size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Premium Quality</h3>
              <p className="text-gray-400">
                4K cinematic production, professional editing, and meticulous attention to detail in every frame.
              </p>
            </Reveal>

            <Reveal delay={3} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 text-center hover:border-cyan-500 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mx-auto mb-6">
                <Award size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Trust & Integrity</h3>
              <p className="text-gray-400">
                Transparent pricing, honest communication, and a commitment to exceeding expectations.
              </p>
            </Reveal>

            <Reveal delay={4} className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 border border-gray-700 text-center hover:border-cyan-500 transition-all duration-300">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-full mx-auto mb-6">
                <Users size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Personal Service</h3>
              <p className="text-gray-400">
                You're not just a client - we work closely with you to bring your vision to life.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="max-w-4xl mx-auto mb-20">
          <Reveal as="h2" className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Why Choose <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Visual Creationz?</span>
          </Reveal>
          
          <div className="space-y-6">
            <Reveal delay={1} className="flex items-start space-x-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-black font-bold">1</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Professional Expertise</h3>
                <p className="text-gray-400">
                  Years of experience in video editing, storytelling, and creating emotional visual narratives.
                </p>
              </div>
            </Reveal>

            <Reveal delay={2} className="flex items-start space-x-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold">2</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Affordable Pricing</h3>
                <p className="text-gray-400">
                  Simple, transparent pricing with no hidden fees. Premium quality doesn't have to mean premium prices.
                </p>
              </div>
            </Reveal>

            <Reveal delay={3} className="flex items-start space-x-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold">3</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Quick Turnaround</h3>
                <p className="text-gray-400">
                  Most projects completed within 3-7 days without sacrificing quality or attention to detail.
                </p>
              </div>
            </Reveal>

            <Reveal delay={4} className="flex items-start space-x-4 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                <span className="text-white font-bold">4</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Personal Touch</h3>
                <p className="text-gray-400">
                  Every cinematic film is unique - your photos, your footage, your soundtrack, your story.
                </p>
              </div>
            </Reveal>
          </div>
        </div>

        {/* CTA */}
        <Reveal className="text-center bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-3xl p-12 border border-cyan-500/30 max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Let's Create Something Beautiful Together
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Ready to preserve your precious memories? We're here to help bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-xl hover:shadow-amber-500/50 hover:scale-105 active:scale-95"
            >
              Create My Film
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-cyan-500 text-cyan-400 font-semibold rounded-full hover:bg-cyan-500 hover:text-white transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default AboutPage;
